import React from 'react'

import localizeDate from '../utils/localizeDate'
import formatCosphi from '../../lib/formatCosphi'
import { WidgetLayout } from '../../lib/widget-layout'
import { MeasurementDeviceExtendedGetModel, MeasurementLatestModel, LoadBalancerGroup } from '../redux/api/web-api'
import { EarthingSchemeLB } from '../enums/earthingSchemes'
import Text from '../../ui/Text'
import useText from '../hooks/useText'

const PowerStatusRow = ({
    isOnline,
    label,
    lastCurrent,
    lastVoltage,
    lastCosphi,
    loadBalancerData,
}: PowerStatusRowProps) => {
    return (
        <tr>
            <td data-testid={`label-${label}`}>{label}</td>
            <td data-testid={`current-${label}`}>
                {isOnline ? (lastCurrent ? `${lastCurrent.toFixed(3)} A` : 0) : '---'}
            </td>
            <td data-testid={`voltage-${label}`}>
                {(lastVoltage && loadBalancerData && loadBalancerData?.earthingSystem !== EarthingSchemeLB.IT) ||
                (lastVoltage && !loadBalancerData)
                    ? `${lastVoltage.toFixed(3)} V`
                    : '---'}
            </td>
            <td data-testid={`signal-${label}`}>
                <span>
                    {(lastCosphi && loadBalancerData && loadBalancerData?.earthingSystem !== EarthingSchemeLB.IT) ||
                    (lastCosphi && !loadBalancerData)
                        ? formatCosphi(lastCosphi)
                        : '---'}
                </span>{' '}
            </td>
        </tr>
    )
}

type PowerStatusRowProps = {
    isOnline: boolean
    label: string
    lastCurrent?: number
    lastVoltage?: number
    lastCosphi?: number
    loadBalancerData?: LoadBalancerGroup
}

const MeasurementDeviceStatusPanel = ({ device, latestMeasurement, loadBalancerData }: Props): React.ReactElement => {
    const text = useText()
    return (
        <WidgetLayout title={text('lblStatus')} data-testid="status">
            <div className="table-container">
                <table data-testid="table">
                    <tbody>
                        <tr>
                            <td data-testid="table-td-status">
                                <Text code="lblStatus" />:
                            </td>
                            <td data-testid="table-tr-status">{device?.status || '---'}</td>
                        </tr>
                        <tr>
                            <td data-testid="table-td-last-measure">
                                <Text code="lblLastMeasurement" />:
                            </td>
                            <td data-testid="table-tr-last-measure">
                                {latestMeasurement?.timestamp ? localizeDate(latestMeasurement?.timestamp) : '---'}
                            </td>
                        </tr>
                        <tr>
                            <td data-testid="table-td-software">
                                <Text code="lblSoftwareVersion" />:
                            </td>
                            <td data-testid="table-tr-software">{latestMeasurement?.version || '---'}</td>
                        </tr>
                        <tr>
                            <td data-testid="table-td-signal">
                                <Text code="lblWiFiSignalStrength" />:
                            </td>
                            <td data-testid="table-tr-signal">
                                <span>
                                    {latestMeasurement?.signalStrength
                                        ? `${latestMeasurement.signalStrength} ${text('lblDecibelMilliwattsShort')}`
                                        : '---'}
                                </span>
                            </td>
                        </tr>
                        <tr>
                            <td colSpan={2}>
                                <h6 data-testid="powerstatus">
                                    <Text code="lblPowerStatus" />:
                                </h6>
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th className="heading5" data-testid="table-th-phase">
                                                <Text code="lblPhase" />
                                            </th>
                                            <th className="heading5" data-testid="table-th-current">
                                                <Text code="lblCurrent" />
                                            </th>

                                            <th className="heading5" data-testid="table-th-voltage">
                                                <Text code="lblVoltage" />
                                            </th>
                                            <th className="heading5" data-testid="table-th-signal">
                                                <Text code="lblPhaseSignal" />
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <PowerStatusRow
                                            isOnline={device?.status === 'Online'}
                                            label="L1"
                                            lastCurrent={latestMeasurement?.current1}
                                            lastVoltage={latestMeasurement?.voltage1}
                                            lastCosphi={latestMeasurement?.cosphi1}
                                            loadBalancerData={loadBalancerData}
                                        />
                                        <PowerStatusRow
                                            isOnline={device?.status === 'Online'}
                                            label="L2"
                                            lastCurrent={latestMeasurement?.current2}
                                            lastVoltage={latestMeasurement?.voltage2}
                                            lastCosphi={latestMeasurement?.cosphi2}
                                            loadBalancerData={loadBalancerData}
                                        />
                                        <PowerStatusRow
                                            isOnline={device?.status === 'Online'}
                                            label="L3"
                                            lastCurrent={latestMeasurement?.current3}
                                            lastVoltage={latestMeasurement?.voltage3}
                                            lastCosphi={latestMeasurement?.cosphi3}
                                            loadBalancerData={loadBalancerData}
                                        />
                                    </tbody>
                                </table>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </WidgetLayout>
    )
}

export default MeasurementDeviceStatusPanel

export type Props = {
    device?: MeasurementDeviceExtendedGetModel
    latestMeasurement?: MeasurementLatestModel
    loadBalancerData?: LoadBalancerGroup
}
